<template>
  <div class="navigation-links">
    <h3 class="typo-headline-6">Navegación</h3>
    <div class="links-container">
      <a href="#" class="nav-link" @click.prevent="goToForumHome">
        <i class="material-icons">home</i>
        Volver al inicio del foro
      </a>
      
      <a 
        v-if="sectionInfo.title && sectionInfo.slug && !isCurrentSection" 
        href="#" 
        class="nav-link"
        @click.prevent="goToSection"
      >
        <i class="material-icons">arrow_back</i>
        Volver a {{ sectionInfo.title }}
      </a>
    </div>
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
const axios = forumApi;

export default {
  name: 'NavigationLinks',
  props: {
    sectionName: {
      type: String,
      default: ''
    },
    sectionSlug: {
      type: String,
      default: ''
    },
    sectionId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      sectionInfo: {
        title: '',
        slug: ''
      },
      loading: false,
      error: null
    };
  },
  computed: {
    // Check if we're already in the section page
    isCurrentSection() {
      return this.$route.name === 'ForumSection' && 
             this.$route.params.slug === this.sectionInfo.slug;
    }
  },
  watch: {
    // Watch for changes in props to update section info
    sectionName(newVal) {
      if (newVal) {
        this.sectionInfo.title = newVal;
      }
    },
    sectionSlug(newVal) {
      if (newVal) {
        this.sectionInfo.slug = newVal;
      }
    },
    sectionId: {
      handler(newVal) {
        if (newVal && (!this.sectionInfo.title || !this.sectionInfo.slug)) {
          this.fetchSectionInfo(newVal);
        }
      },
      immediate: true
    }
  },
  mounted() {
    // If we have a section name and slug from props, use them
    if (this.sectionName && this.sectionSlug) {
      this.sectionInfo.title = this.sectionName;
      this.sectionInfo.slug = this.sectionSlug;
    } 
    // If we have a section ID but no name/slug, fetch the info
    else if (this.sectionId && (!this.sectionInfo.title || !this.sectionInfo.slug)) {
      this.fetchSectionInfo(this.sectionId);
    }
    // If we're in a thread page, try to get section info from the thread
    else if (this.$route.name === 'ForumThread') {
      this.fetchThreadSectionInfo();
    }
    // If we're in a section page, get the section info from the route
    else if (this.$route.name === 'ForumSection') {
      this.fetchSectionInfoFromSlug(this.$route.params.slug);
    }
  },
  methods: {
    goToForumHome() {
      this.$router.push({name: 'Forum'});
    },
    
    goToSection() {
      if (this.sectionInfo.slug) {
        this.$router.push({ 
          name: 'ForumSection', 
          params: { slug: this.sectionInfo.slug } 
        });
      }
    },
    
    fetchSectionInfo(sectionId) {
      this.loading = true;
      this.error = null;
      
      axios.get('/forum/api/structure') // Fetch the new structure
        .then(response => {
          if (response.data) {
            const forumStructure = response.data;
            let foundSection = null;

            // Function to search the structure recursively
            const findSectionById = (structure, targetId) => {
              for (const tid in structure) {
                const item = structure[tid];
                if (parseInt(item.tid) === parseInt(targetId)) {
                  return item;
                }
                if (item.children && Object.keys(item.children).length > 0) {
                  const foundInChildren = findSectionById(item.children, targetId);
                  if (foundInChildren) return foundInChildren;
                }
              }
              return null;
            };

            foundSection = findSectionById(forumStructure, sectionId);

            if (foundSection) {
              this.sectionInfo.title = foundSection.name;
              this.sectionInfo.slug = foundSection.slug;
            } else {
              this.error = 'Parent section info not found';
              console.error('Could not find section with ID:', sectionId, 'in structure:', forumStructure);
            }
          } else {
             this.error = 'Invalid forum structure response';
             throw new Error('Invalid forum structure response');
          }
        })
        .catch(err => {
          this.error = 'Error fetching forum structure';
          console.error('Error fetching forum structure:', err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchThreadSectionInfo() {
      const threadId = this.$route.params.nid;
      if (!threadId) return;
      
      axios.get(`/forum/thread/${threadId}`)
        .then(response => {
          if (response.data && response.data.thread) {
            const thread = response.data.thread;
            if (thread.forum_tid) {
              // If we have the forum name directly in the thread data
              if (thread.forum_name) {
                this.sectionInfo.title = thread.forum_name;
              }
              
              // If we have the forum slug directly in the thread data
              if (thread.forum_slug) {
                this.sectionInfo.slug = thread.forum_slug;
              } 
              // Otherwise fetch the section info using the ID
              else {
                this.fetchSectionInfo(thread.forum_tid);
              }
            }
          }
        })
        .catch(err => {
          console.error('Error fetching thread section info:', err);
        });
    },
    fetchSectionInfoFromSlug(slug) {
      if (!slug) return;
      this.loading = true;
      this.error = null;

      axios.get(`/api/forum-section/slug/${slug}`)
        .then(response => {
          if (response.data && response.data.success && response.data.section) {
            const section = response.data.section;
            this.sectionInfo.title = section.title;
            this.sectionInfo.slug = section.slug;
          } else {
            this.error = 'Section info not found for slug';
            console.error('Section info not found for slug:', slug);
          }
        })
        .catch(err => {
          this.error = 'Error fetching section info';
          console.error('Error fetching section info from slug:', err);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.navigation-links {
  background-color: var(--color-surface);
  padding: 20px;
  border-radius: 15px;
  color: var(--color-text-primary);
}

.navigation-links h3 {
  margin-top: 0;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-outline);
}

.links-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-highlight);
  text-decoration: none;
  padding: 8px 0;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: var(--color-highlight-dark, #1976d2);
}

.nav-link i {
  font-size: 18px;
}
</style> 